import { Column, Row, SizedBox } from "../../../common/components/CommonBaseComponents";
import { CustomTable } from "../../../common/components/table/CustomTable";
import CustomInput from "../../../common/components/input/CustomInput";
import SecondaryButton from "../../../common/components/button/SecondaryButton";
import ClickableNetworkImage from "../../../common/components/image/ClickableNetworkImage";
import { useEffect, useRef, useState } from "react";
import Translate from "../../../common/translate/Translate";
import SettingsService from "../../../service/SettingsService";
import { useSearchParams } from "react-router-dom";
import CustomPopup from "../../../common/components/popup/CustomPopup";
import { Theme } from "../../../common/Theme";

// TODO: Course Image 목록 조회 API가 별도로 있는지 확인 필요.
// 현재 SettingsService에는 uploadCourseImage / deleteCourseImage만 있고, 목록 조회 API가 없음.
// 목록 조회 API가 추가되면 mockData를 교체할 것.
const fallbackMockData = [
    {
        number: "1",
        currentVersion: "1",
        version: "",
        url: "Course Image.jpg",
        image: "https://picsum.photos/id/100/200/300",
    },
    {
        number: "1",
        currentVersion: "1",
        version: "1.5",
        url: "Course Image.jpg",
        image: "https://picsum.photos/id/102/200/300",
    },
    {
        number: "1",
        currentVersion: "1",
        version: "1.5",
        url: "Course Image.jpg",
        image: "https://picsum.photos/id/103/200/300",
    },
    {
        number: "1",
        currentVersion: "1",
        version: "",
        url: "Course Image.jpg",
        image: "https://picsum.photos/id/100/200/300",
    },
    {
        number: "1",
        currentVersion: "1",
        version: "1.5",
        url: "Course Image.jpg",
        image: "https://picsum.photos/id/100/200/300",
    },
    {
        number: "1",
        currentVersion: "1",
        version: "1.5",
        url: "Course Image.jpg",
        image: "https://picsum.photos/id/100/200/300",
    },
    {
        number: "1",
        currentVersion: "1",
        version: "1.5",
        url: "Course Image.jpg",
        image: "https://picsum.photos/id/100/200/300",
    },
    {
        number: "1",
        currentVersion: "1",
        version: "",
        url: "Course Image.jpg",
        image: "https://picsum.photos/id/100/200/300",
    },
    {
        number: "1",
        currentVersion: "1",
        version: "",
        url: "Course Image.jpg",
        image: "https://picsum.photos/id/100/200/300",
    },
    {
        number: "1",
        currentVersion: "1",
        version: "1.5",
        url: "Course Image.jpg",
        image: "https://picsum.photos/id/100/200/300",
    },
];

function CourseImageTabContent() {
    const service = new SettingsService();
    const [tableData, setTableData] = useState(fallbackMockData);
    const [searchParams, setSearchParams] = useSearchParams();
    const page = searchParams.get("page") || 1;

    const isMountedRef = useRef(true);

    // 이미지 삭제 확인 팝업 상태 (삭제 대상 행 정보 보관)
    const [pendingDelete, setPendingDelete] = useState(null);

    // 이미지 업로드(교체)용 파일 input 및 업로드 대상 행 정보
    const fileInputRef = useRef(null);
    const uploadTargetRef = useRef(null);

    // 파일 용량 초과 알럿 (500KB 제한, CourseAddImageModal과 동일)
    const [isSizePopupOpen, setIsSizePopupOpen] = useState(false);

    const handleUploadClick = (row) => {
        uploadTargetRef.current = {
            imageNum: row.imageNum ?? row.number,
            version: row.version ?? row.currentVersion,
        };
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleFileSelected = (event) => {
        const files = event.target.files;
        const target = uploadTargetRef.current;
        // 같은 파일 재선택 시에도 onChange가 동작하도록 input 값 초기화
        event.target.value = "";

        if (!files || files.length === 0 || !target) return;

        // 500KB(512000바이트) 초과 시 업로드하지 않고 알럿 표시
        if (files[0].size >= 512000) {
            setIsSizePopupOpen(true);
            return;
        }

        service
            .uploadCourseImage({ imageNum: target.imageNum, version: target.version, content: files[0] })
            .then(() => {
                getCurseImages();
            })
            .catch((error) => {
                console.error("Error uploading course image:", error);
            });
    };

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const handleDeleteCourseImage = (imageNum, version, rowIndex) => {
        service
            .deleteCourseImage({ imageNum, version })
            .then(() => {
                setTableData((currentData) => currentData.filter((_, index) => index !== rowIndex));
            })
            .catch((error) => {
                console.error("Error deleting course image:", error);
            });
    };

    const handleConfirmDelete = () => {
        if (pendingDelete) {
            handleDeleteCourseImage(pendingDelete.imageNum, pendingDelete.version, pendingDelete.rowIndex);
        }
        setPendingDelete(null);
    };

    const handleVersionChange = (rowIndex, newVersion) => {
        setTableData((currentData) =>
            currentData.map((row, index) => {
                if (index === rowIndex) {
                    return { ...row, version: newVersion };
                }
                return row;
            })
        );
    };

    const getCurseImages = () => {
        service.getCourseImages({ page: page }).then((result) => {
            if (!isMountedRef.current) return;
            const existingParams = Object.fromEntries(searchParams.entries());
            setSearchParams({ ...existingParams, totalPage: result.page.total }, { replace: true });
            setTableData(result.imageList);
        });
    };

    useEffect(() => {
        getCurseImages();
    }, [page]);

    const tableHeaders = [
        {
            key: "imageNum",
            label: Translate("common.number"),
        },
        {
            key: "version",
            label: Translate("setting.currentVersion"),
        },
        {
            key: "version",
            label: Translate("setting.version"),
            render: (row, rowIndex) => {
                return (
                    <CustomInput
                        type={"normal"}
                        placeholder={Translate("setting.version")}
                        inputValue={row.version} // state의 현재 값을 표시
                        setInputValue={(newVersion) => handleVersionChange(rowIndex, newVersion)}
                    />
                );
            },
        },
        {
            key: "url",
            label: Translate("setting.url"),
            render: (row, rowIndex) => (
                <Row>
                    <CustomInput
                        type={"normal"}
                        placeholder={"Version"}
                        inputValue={row.url}
                        disabled={true}
                        onDelete={() =>
                            setPendingDelete({
                                imageNum: row.imageNum ?? row.number,
                                version: row.version ?? row.currentVersion,
                                rowIndex,
                                message: Translate("popup.deletePhoto"),
                            })
                        }
                    />
                    <SizedBox width={"10px"} />
                    <SecondaryButton
                        icon={"upload"}
                        text={Translate("common.upload")}
                        onClick={() => handleUploadClick(row)}
                    />
                </Row>
            ),
        },
        {
            key: "image",
            label: Translate("common.image"),
            render: (row) => (
                <Row justifyContent={"center"}>
                    <ClickableNetworkImage src={row.url} width={"68px"} height={"68px"} borderRadius={"12px"} />
                </Row>
            ),
        },
        {
            key: "action",
            label: "",
            render: (row, rowIndex) => (
                <Row justifyContent={"flex-end"}>
                    <SecondaryButton
                        text={Translate("common.delete")}
                        onClick={() =>
                            setPendingDelete({
                                imageNum: row.imageNum ?? row.number,
                                version: row.version ?? row.currentVersion,
                                rowIndex,
                                message: Translate("popup.deleteItem"),
                            })
                        }
                    />
                    <SizedBox width={"14px"} />
                </Row>
            ),
        },
    ];

    return (
        <Column width={"100%"} padding={"32px 0"}>
            <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleFileSelected}
            />
            <CustomTable headers={tableHeaders} data={tableData} hasColumnBorder={false} />
            <CustomPopup
                isOpen={pendingDelete !== null}
                onClose={() => setPendingDelete(null)}
                onConfirm={handleConfirmDelete}
                message={pendingDelete?.message ?? Translate("popup.deleteItem")}
                confirmText={Translate("common.delete")}
                confirmColor={Theme.color.rose500}
            />
            <CustomPopup
                isOpen={isSizePopupOpen}
                onConfirm={() => setIsSizePopupOpen(false)}
                message={"The file is too large. Please upload a smaller file."}
            />
        </Column>
    );
}

export default CourseImageTabContent;
